/** How an attachment should be previewed. */
export type AttachmentKind = 'image' | 'pdf' | 'excel' | 'archive' | 'other';

/**
 * Decide which viewer an attachment needs.
 *
 * MIME type first, filename extension as the fallback — inbound email attachments
 * routinely arrive as `application/octet-stream` regardless of what they actually
 * are, so trusting the MIME alone would send most supplier workbooks down the
 * "unknown file" path and hide exactly the decision sheet the reviewer opened it for.
 *
 * Pure and separate from the component so the mapping is unit-testable and the
 * component stays a straight switch.
 */
export function attachmentKind(
  mimeType: string | null | undefined,
  fileName: string,
): AttachmentKind {
  const mime = (mimeType ?? '').toLowerCase();

  if (mime.startsWith('image/')) return 'image';
  if (mime.includes('pdf') || /\.pdf$/i.test(fileName)) return 'pdf';
  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    /\.(xlsx|xls|xlsm)$/i.test(fileName)
  ) {
    return 'excel';
  }
  // A zip opens as its list of files, so the reviewer can pick the ones that
  // answer the claim and attach them to the email. Office files are zips too and
  // are sometimes sent with a zip MIME; their extension says what they are.
  if (
    /\.zip$/i.test(fileName) ||
    (mime.includes('zip') && !/\.(docx|pptx)$/i.test(fileName))
  ) {
    return 'archive';
  }
  if (/\.(png|jpe?g|gif|webp|bmp)$/i.test(fileName)) return 'image';
  return 'other';
}
